import JSZip from "jszip";
import { meshStats, validateSTL, weldVertices, writeSTLAscii, writeSTLBinary, type STLCheck } from "@/lib/mesh3d";
import { uniqueNames } from "../file/zipUtils";

export type StlTarget = "ascii" | "binary";

export interface StlItem {
  name: string;
  bytes: ArrayBuffer;
  check: STLCheck;
}

/** Reads one chosen file and validates it. Never throws: an unreadable file becomes a "Not a valid STL" item. */
export async function readStlItem(file: File): Promise<StlItem> {
  try {
    const bytes = await file.arrayBuffer();
    return { name: file.name, bytes, check: validateSTL(bytes) };
  } catch {
    return { name: file.name, bytes: new ArrayBuffer(0), check: { ok: false, reason: "Not a valid STL: the file could not be read." } };
  }
}

export const baseName = (n: string) => n.replace(/\.stl$/i, "");

/** True when there is at least one valid file and the target would change none of them. */
export function targetIsRedundant(items: StlItem[], to: StlTarget): boolean {
  const valid = items.filter((i) => i.check.ok);
  return valid.length > 0 && valid.every((i) => i.check.ok && i.check.format === to);
}

/**
 * Converts every valid file. One valid file gives one .stl; several give one .zip. A file that is already in
 * the target format is carried over byte for byte (its name keeps the suffix) rather than rewritten.
 * Invalid files are skipped and listed, never silently dropped.
 */
export async function convertStlBatch(items: StlItem[], to: StlTarget) {
  const valid = items.filter((i): i is StlItem & { check: Extract<STLCheck, { ok: true }> } => i.check.ok);
  const skipped = items.filter((i) => !i.check.ok).map((i) => i.name);
  if (valid.length === 0) throw new Error("There is no valid STL file to convert.");
  const outNames = uniqueNames(valid.map((i) => `${baseName(i.name)}_${to}.stl`));
  const parts = valid.map((i, n) => {
    const same = i.check.format === to;
    const mesh = i.check.mesh;
    const bytes: ArrayBuffer | string = same ? i.bytes : to === "binary" ? writeSTLBinary(mesh) : writeSTLAscii(mesh, baseName(i.name));
    return { name: outNames[n], bytes, same, stats: meshStats(weldVertices(mesh)), triangles: i.check.triangleCount };
  });
  const type = "application/sla";
  const single = parts.length === 1;
  let blob: Blob;
  let name: string;
  if (single) {
    blob = new Blob([parts[0].bytes], { type });
    name = parts[0].name;
  } else {
    const zip = new JSZip();
    parts.forEach((p) => zip.file(p.name, p.bytes));
    blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
    name = `stl_${to}_${parts.length}_files.zip`;
  }
  return { blob, name, format: to, skipped, files: parts.map((p) => ({ name: p.name, size: p.bytes instanceof ArrayBuffer ? p.bytes.byteLength : new Blob([p.bytes]).size, same: p.same, triangles: p.triangles })), stats: parts[0].stats, single };
}
